/**
 * Badge — 라벨/태그용 인라인(span) 컴포넌트.
 *
 * - variant : 'recommend' | 'interior' — 사전 정의된 bg/color 프리셋
 * - size    : xlarge~small, [pc, mo] 튜플로 PC/MO 반응형 지정 가능 (기본 xlarge)
 * - radius  : 'max' 지정 시 size별 border-radius 대신 알약 모양으로 오버라이드
 * - bgColor / textColor : resolveColor로 정규화 후 --badge-bg / --badge-color 에 매핑
 *   (페이지 SCSS에서 위 CSS 변수로 직접 오버라이드도 가능)
 *
 * BadgeGroup — 여러 Badge를 감싸는 flex 컨테이너. vertical / align(left·center·right) 정렬 제어.
 *
 * @example
 * <Badge variant="recommend" size={['xlarge', 'small']}>추천</Badge>
 * <BadgeGroup align="center"><Badge>A</Badge><Badge>B</Badge></BadgeGroup>
 */
import clsx from 'clsx';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { resolveColor } from '@publishing/helpers/format';
import { resolveResponsiveValue, type ResponsiveValue } from '@publishing/helpers/responsive';
import s from './Badge.module.scss';

export type BadgeVariant = 'recommend' | 'interior';
export type BadgeSize = 'xlarge' | 'large' | 'medium' | 'small';
export type BadgeResponsiveSize = ResponsiveValue<BadgeSize>;
// 상속되는 레거시 HTML color 속성을 막아 textColor 사용을 유도 (안 막으면 rest로 span에 전개)
interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  size?: BadgeResponsiveSize;
  radius?: 'max';
  variant?: BadgeVariant;
  bgColor?: string;
  textColor?: string;
  className?: string;
  children?: ReactNode;
}

interface BadgeGroupProps {
  children: ReactNode;
  vertical?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export default function Badge({
  size = 'xlarge',
  radius,
  variant,
  bgColor,
  textColor,
  className,
  children,
  style,
  ...rest
}: BadgeProps) {
  const [pcSize, moSize] = resolveResponsiveValue(size);

  const customStyle = {
    '--badge-bg': bgColor ? resolveColor(bgColor) : undefined,
    '--badge-color': textColor ? resolveColor(textColor) : undefined,
    borderRadius: radius && `var(--radius-${radius})`,
    ...style,
  } as CSSProperties;

  return (
    <span
      {...rest}
      className={clsx(s.badge, s[`badge_${pcSize}`], moSize && s[`badge_mo_${moSize}`], variant && s[`badge_${variant}`], className)}
      style={customStyle}
    >
      {children}
    </span>
  );
}

export function BadgeGroup({ children, vertical, align = 'left', className }: BadgeGroupProps) {
  return (
    <div className={clsx(s.badge_group, vertical && s.badge_group_vertical, s[`badge_group_${align}`], className)}>
      {children}
    </div>
  );
}
